import React, { Component } from 'react';
import PropTypes from 'prop-types';
import { withTranslation } from 'react-i18next';
import {
  BadgeAlt,
  Button,
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from '@cargomatic/ui';
import CargomaticCollapsibleCard from 'components/v2/CollapsibleCard';
import { Download, Loader2 } from 'lucide-react';
import OrdersActionCreators from 'actions/OrdersActionCreators';
import OrdersAccountingStore from 'stores/OrdersAccountingStore';
import formatMoney from 'utils/formatters/Money';
import { getDocumentsV2, getSignedUrls } from 'utils/DocumentsAPIUtils';
import GenerateLedgerModal from './GenerateLedgerModal';
import { getUserInvoiceSettings } from 'utils/UserServiceAPIUtils';
import ChargeItem from './ChargeItem';
import { RECEIVABLES, PAYABLES } from '../../../../constants/LedgerTypes';

function needsProcessing(ledgerType, shipment) {
  if (ledgerType === 'receivables') {
    return shipment.needs_invoice;
  }
  return shipment.needs_payout;
}

class Accounting extends Component {
  constructor(props) {
    super(props);
    this.state = {
      paymentPending: false,
      showGenerateLedgerModal: false,
      shipperInvoiceSettings: null,
      pdfUrls: {},
    };
  }

  async componentDidMount() {
    OrdersAccountingStore.addChangeListener(
      this.handleOrdersAccountingStoreChange,
    );
    this.fetchSignedUrls(this.props.order);
    await this.fetchShipperInvoiceSettings();
  }

  UNSAFE_componentWillReceiveProps(nextProps) {
    this.fetchSignedUrls(nextProps.order);
  }

  componentWillUnmount() {
    OrdersAccountingStore.removeChangeListener(
      this.handleOrdersAccountingStoreChange,
    );
  }

  fetchShipperInvoiceSettings = async () => {
    const shipper = this.props.order.shipper;
    const shipperId = shipper._id ? shipper._id : shipper;
    if (shipperId) {
      try {
        const invoiceSettingsResult = await getUserInvoiceSettings(shipperId);
        const invoiceSettings =
          invoiceSettingsResult?.data?.getInvoiceSetting?.invoice_setting;
        this.setState({
          shipperInvoiceSettings: invoiceSettings,
        });
      } catch (e) {
        this.setState({
          shipperInvoiceSettings: null,
        });
        console.error(e);
      }
    } else {
      console.error('SHIPPER_NOT_FOUND_ON_ORDER');
    }
  };

  async fetchSignedUrls(order) {
    try {
      const pdfUrls = {};
      let changed = false;

      const ids = [
        ...order.invoices.map((invoice) => invoice._id),
        ...order.payouts.map((payout) => payout._id),
      ];

      if (!ids.length) {
        return;
      }

      const getDocumentsResponse = await getDocumentsV2(ids);
      const documents = getDocumentsResponse.data?.getDocumentsV2.documents;
      const getSignedUrlsResponse = await getSignedUrls(ids);
      const signedUrls = getSignedUrlsResponse.data?.getSignedUrls.signedUrls;

      documents.forEach((document) => {
        document.foreignKeys.forEach((ledgerId) => {
          if (this.state.pdfUrls[ledgerId]?.createdAt !== document.createdAt) {
            changed = true;

            pdfUrls[ledgerId] = {
              signedUrl: signedUrls.find(
                (signedUrl) => document.id === signedUrl.id,
              ).signedUrl,
              createdAt: document.createdAt,
            };
          }
        });
      });

      if (changed) {
        this.setState({ pdfUrls });
      }
    } catch (e) {
      console.error('orders/accounting, failed to get signed URL: ', e);
    }
  }

  getOutstandingTotal() {
    const { ledgerType, order } = this.props;

    if (ledgerType === 'receivables') {
      return order.outstanding_ar;
    }
    return order.outstanding_ap;
  }

  getChargeItems() {
    const { ledgerType, shipments } = this.props;
    return shipments.filter(
      (job) => needsProcessing(ledgerType, job) && !job.canceled,
    );
  }

  getLedgerType() {
    const { ledgerType, t } = this.props;
    if (ledgerType === RECEIVABLES) {
      return t('INVOICE_NUMBER');
    }
    return t('PAYOUT_NUMBER');
  }

  getInvoices() {
    const { order } = this.props;
    return order.invoices || [];
  }

  getPayouts() {
    const { order } = this.props;
    return order.payouts || [];
  }

  getLedgerItems() {
    const { ledgerType } = this.props;

    let ledgerItems = [];

    if (ledgerType === RECEIVABLES) {
      ledgerItems = ledgerItems.concat(this.getInvoices());
    } else {
      ledgerItems = ledgerItems.concat(this.getPayouts());
    }
    return ledgerItems;
  }

  getPaymentButtonText() {
    const { ledgerType, t } = this.props;
    const action = ledgerType === RECEIVABLES ? 'INVOICE' : 'PAYOUT';

    return t('CREATE_WITH_VALUE', { action: t(action, { count: 1 }) });
  }

  getSectionTitle() {
    const { ledgerType, t } = this.props;
    if (ledgerType === RECEIVABLES) {
      return t('ACCOUNT_RECEIVABLE');
    }
    return t('ACCOUNT_PAYABLE');
  }

  paymentActionsEnabled() {
    const { ledgerType } = this.props;
    const shipments = this.props.shipments || [];
    const field = ledgerType === RECEIVABLES ? 'needs_invoice' : 'needs_payout';
    return shipments.find((s) => s[field] && !s.canceled) !== undefined;
  }

  handleOrdersAccountingStoreChange = () => {
    this.setState({
      paymentPending: false,
    });
  };

  handleGenerateLedger = (
    ledgerDate,
    selectedCharges,
    sendEdi = false,
    sendEmail = false,
  ) => {
    const {
      order: { _id: orderId },
      ledgerType,
    } = this.props;
    this.setState({
      paymentPending: true,
      showGenerateLedgerModal: false,
    });

    if (ledgerType === RECEIVABLES) {
      OrdersActionCreators.generateOrderInvoice(
        orderId,
        ledgerDate,
        selectedCharges,
        sendEdi,
        sendEmail,
      );
    } else {
      OrdersActionCreators.generateOrderPayout(
        orderId,
        ledgerDate,
        selectedCharges,
        true,
      );
    }
  };

  handleOpenGenerateLedgerModal = () =>
    this.setState({ showGenerateLedgerModal: true });

  handleCloseGenerateLedgerModal = () =>
    this.setState({ showGenerateLedgerModal: false });

  renderPaymentActions() {
    if (!this.paymentActionsEnabled()) {
      return null;
    }
    const outstandingTotal = this.getOutstandingTotal();
    const { t } = this.props;
    return (
      <div className="flex items-center justify-between">
        <div className="flex items-center gap-2">
          <span className="text-sm font-medium">{t('TOTAL_OUTSTANDING')}:</span>
          <span className="text-sm">
            {formatMoney(outstandingTotal, this.props.order?.currency)}
          </span>
        </div>
        <Button
          variant="outline"
          size="sm"
          disabled={this.state.paymentPending}
          onClick={this.handleOpenGenerateLedgerModal}
        >
          {this.state.paymentPending && (
            <Loader2 className="h-4 w-4 mr-1 animate-spin" />
          )}
          {this.getPaymentButtonText()}
        </Button>
      </div>
    );
  }

  renderLedgers() {
    const { ledgerType, t } = this.props;
    const ledgerItems = this.getLedgerItems();
    if (ledgerItems.length === 0) {
      return null;
    }

    const baseUrl = ledgerType === 'receivables' ? '/invoices' : '/payouts';

    return (
      <div className="mt-4">
        <Table>
          <TableHeader>
            <TableRow>
              <TableHead className="whitespace-nowrap">
                {this.getLedgerType()}
              </TableHead>
              <TableHead />
              <TableHead />
            </TableRow>
          </TableHeader>
          <TableBody>
            {ledgerItems.map((ledger) => {
              const ledgerDetailLink = `${baseUrl}/${ledger._id}`;
              const pdf = this.state.pdfUrls[ledger._id]?.signedUrl;
              const emailSent =
                ledgerType === 'receivables' && ledger.email_sent;

              return (
                <TableRow key={ledger._id}>
                  <TableCell className="whitespace-nowrap">
                    <a href={ledgerDetailLink} className="text-primary">
                      {ledger.number}
                    </a>
                  </TableCell>
                  <TableCell className="whitespace-nowrap">
                    <div className="flex items-center gap-1">
                      {ledger.edi_sent && (
                        <BadgeAlt
                          variant="outline"
                          className="bg-green-100 text-green-800 border-green-300 text-xs"
                        >
                          {t('EDI_SENT')}
                        </BadgeAlt>
                      )}
                      {emailSent && (
                        <BadgeAlt
                          variant="outline"
                          className="bg-green-100 text-green-800 border-green-300 text-xs"
                        >
                          {t('INVOICE_SENT')}
                        </BadgeAlt>
                      )}
                    </div>
                  </TableCell>
                  <TableCell className="whitespace-nowrap text-right">
                    {pdf && (
                      <Button
                        variant="ghost"
                        size="icon"
                        className="h-8 w-8"
                        asChild
                      >
                        <a
                          href={pdf}
                          target="_blank"
                          rel="noopener noreferrer"
                          title={t('DOWNLOAD')}
                        >
                          <Download className="h-4 w-4" />
                        </a>
                      </Button>
                    )}
                  </TableCell>
                </TableRow>
              );
            })}
          </TableBody>
        </Table>
      </div>
    );
  }

  render() {
    const { ledgerType } = this.props;
    return (
      <CargomaticCollapsibleCard
        id={`order-accounting-${ledgerType}`}
        header={() => <span>{this.getSectionTitle()}</span>}
      >
        <div className="pb-4">{this.renderPaymentActions()}</div>
        <div>
          {this.getChargeItems().map((shipment, i) => (
            <ChargeItem
              isEven={(i + 1) % 2 === 0}
              key={shipment._id}
              shipment={shipment}
              ledgerType={ledgerType}
              currency={this.props.order?.currency}
            />
          ))}
        </div>
        {this.renderLedgers()}
        {this.state.showGenerateLedgerModal && (
          <GenerateLedgerModal
            show={this.state.showGenerateLedgerModal}
            equipment={this.props.order.equipments}
            shipper={this.props.order.shipper}
            carrier={this.props.order.carrier}
            orderId={this.props.order._id}
            shipments={this.props.shipments}
            ledgerType={ledgerType}
            onGenerateLedger={this.handleGenerateLedger}
            onClose={this.handleCloseGenerateLedgerModal}
            shipperInvoiceSettings={this.state.shipperInvoiceSettings}
          />
        )}
      </CargomaticCollapsibleCard>
    );
  }
}

Accounting.propTypes = {
  order: PropTypes.object,
  ledgerType: PropTypes.oneOf([RECEIVABLES, PAYABLES]),
};

export default withTranslation()(Accounting);
